// src/chef/KitchenItemsGrouped.js
import React, { useContext } from "react";
import { TimezoneContext } from "../context/TimezoneContext";
import { formatUtcToTz } from "../utils/datetime";

/**
 * KitchenItemsGrouped (chef dashboard)
 * ---------------------------------------------------------------------------
 * • Mostra SOLTANTO i piatti degli ordini destinati alla cucina (is_kitchen_order === true)
 *   e già inviati (sent_to_kitchen_at !== null).
 * • Raggruppa prima per tavolo, poi per ordine.
 * • Porta in cima i piatti con commento.
 * • Pulsanti "Inizia" / "Pronto" visibili solo se il piatto è stato effettivamente
 *   mandato in cucina.
 */
export default function KitchenItemsGrouped({ items = [], onUpdateItemStatus }) {
  /* ----- fuso orario selezionato (provider globale) ----- */
  const { timezone } = useContext(TimezoneContext);

  /* ----- filtriamo: solo piatti che vanno in cucina e già spediti ----- */
  const kitchenItems = items.filter(
    (it) =>
      (it.is_kitchen_order ?? it.order_is_kitchen_order ?? true) &&
      Boolean(it.sent_to_kitchen_at)
  );

  const groupedByTable = groupByTable(kitchenItems);

  return (
    <div style={styles.wrapper}>
      <h2 style={styles.subtitle}>Piatti da cucinare (raggruppati per tavolo)</h2>

      {Object.keys(groupedByTable).length === 0 ? (
        <p style={styles.noItems}>Nessun piatto in attesa / in preparazione</p>
      ) : (
        Object.entries(groupedByTable).map(([tableNumber, tableItems]) => (
          <TableItemsGroup
            key={tableNumber}
            tableNumber={tableNumber}
            items={tableItems}
            onUpdateItemStatus={onUpdateItemStatus}
            timezone={timezone}
          />
        ))
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*                               SUB‑COMPONENTI                               */
/* -------------------------------------------------------------------------- */

function TableItemsGroup({ tableNumber, items, onUpdateItemStatus, timezone }) {
  const groupedByOrder = groupByOrder(items);

  return (
    <div style={styles.tableBlock}>
      <h3 style={styles.tableTitle}>Tavolo {tableNumber}</h3>

      {Object.keys(groupedByOrder).map((orderId) => {
        const orderItems = groupedByOrder[orderId];

        /* — sort: commento prima, poi ora di invio — */
        orderItems.sort((a, b) => {
          const cA = Boolean(a.comment && a.comment.trim());
          const cB = Boolean(b.comment && b.comment.trim());
          if (cA !== cB) return cB - cA;
          const tA = new Date(a.sent_to_kitchen_at).getTime() || 0;
          const tB = new Date(b.sent_to_kitchen_at).getTime() || 0;
          return tA - tB;
        });

        const firstItem = orderItems[0];

        return (
          <div key={orderId} style={styles.orderBlock}>
            <h4 style={styles.orderTitle}>Ordine #{orderId}</h4>
            <p style={styles.sentTime}>
              Inviato in cucina:{" "}
              {firstItem?.sent_to_kitchen_at
                ? formatUtcToTz(firstItem.sent_to_kitchen_at, timezone)
                : "-"}
            </p>

            {orderItems.map((it) => (
              <KitchenItemRow
                key={it.id}
                item={it}
                onUpdateItemStatus={onUpdateItemStatus}
              />
            ))}
          </div>
        );
      })}
    </div>
  );
}

function KitchenItemRow({ item, onUpdateItemStatus }) {
  const hasComment = Boolean(item.comment && item.comment.trim());

  return (
    <div
      style={{
        ...styles.itemRow,
        backgroundColor: hasComment ? "#FFF9E8" : styles.itemRow.backgroundColor,
      }}
    >
      <div>
        <strong>
          {hasComment && <span style={styles.commentIcon}>💬 </span>}
          {item.menu_item_name}
        </strong>{" "}
        x {item.quantity}
        {item.menu_item_category && (
          <span style={styles.category}>({item.menu_item_category})</span>
        )}
        {hasComment && (
          <em style={styles.commentText}> – {item.comment}</em>
        )}
      </div>

      <div style={styles.buttonsWrapper}>
        {item.status === "pending" && (
          <button
            style={styles.inProgressButton}
            onClick={() => onUpdateItemStatus(item.id, "in_progress")}
          >
            Inizia
          </button>
        )}

        {item.status === "in_progress" && (
          <button
            style={styles.readyButton}
            onClick={() => onUpdateItemStatus(item.id, "ready")}
          >
            Pronto
          </button>
        )}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*                                 HELPERS                                    */
/* -------------------------------------------------------------------------- */

function groupByTable(items) {
  return items.reduce((acc, it) => {
    const key = it.table_number || "SenzaTavolo";
    if (!acc[key]) acc[key] = [];
    acc[key].push(it);
    return acc;
  }, {});
}

function groupByOrder(items) {
  return items.reduce((acc, it) => {
    if (!acc[it.order_id]) acc[it.order_id] = [];
    acc[it.order_id].push(it);
    return acc;
  }, {});
}

/* -------------------------------------------------------------------------- */
/*                                   STILI                                    */
/* -------------------------------------------------------------------------- */

const palette = {
  primary:   "#FF7F00",
  success:   "#32CD32",
  greyLight: "#f9f9f9",
  textDark:  "#333",
  textLight: "#FFF",
};

const styles = {
  wrapper: {
    backgroundColor: palette.textLight,
    borderRadius:    6,
    padding:         20,
    boxShadow:       "0 2px 5px rgba(0,0,0,0.1)",
    fontSize:        16,
  },
  subtitle:  { fontSize: 18, marginBottom: 15, fontWeight: "bold", color: palette.textDark },
  noItems:   { fontSize: 14, color: "#666" },
  tableBlock: {
    border:        "1px solid #ddd",
    borderRadius:  6,
    padding:       15,
    marginBottom:  20,
    backgroundColor: palette.greyLight,
  },
  tableTitle: { marginBottom: 10, fontSize: 16, fontWeight: "bold", color: palette.textDark },
  orderBlock: {
    backgroundColor: "#fff",
    border:          "1px solid #ccc",
    borderRadius:    6,
    padding:         10,
    marginBottom:    15,
  },
  orderTitle: { margin: 0, marginBottom: 5, fontSize: 14, fontWeight: "bold", color: palette.textDark },
  sentTime:   { fontSize: 14, marginBottom: 10, color: "#555" },
  itemRow: {
    display:       "flex",
    justifyContent:"space-between",
    alignItems:    "center",
    marginBottom:  8,
    backgroundColor: palette.greyLight,
    border:          "1px solid #eee",
    borderRadius:    4,
    padding:         8,
  },
  commentIcon:    { marginRight: 4 },
  category:       { marginLeft: 5, color: "#999", fontSize: 12 },
  commentText:    { marginLeft: 4, fontSize: 13, color: "#555" },
  buttonsWrapper: { display: "flex", gap: 8, alignItems: "center" },
  inProgressButton: {
    backgroundColor: palette.primary,
    border:          "none",
    padding:         "8px 12px",
    borderRadius:    4,
    color:           palette.textLight,
    fontWeight:      "bold",
    fontSize:        14,
    cursor:          "pointer",
  },
  readyButton: {
    backgroundColor: palette.success,
    border:          "none",
    padding:         "8px 12px",
    borderRadius:    4,
    color:           palette.textLight,
    fontWeight:      "bold",
    fontSize:        14,
    cursor:          "pointer",
  },
};
